import { LANE_IDS, type GameAction, type GameState, type PlayerId } from "../types";
import {
  getArmyDefinitionV3ForInstance,
  getCardInstanceDefinition,
  getCommanderDefinitionForInstance,
  getEventDefinitionForInstance,
  isSetupGameState,
} from "../selectors/card-instances";
import { getSupplySnapshot } from "../selectors/supply";
import { getActiveLandLaneIds, getAttackKind } from "../selectors/formation";
import { hasFoundingPriority } from "../validation/validate-land-action";
import { validateAction } from "../validation/validate-action";

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

export function getLegalActions(
  state: Readonly<GameState>,
  playerId: PlayerId,
): readonly GameAction[] {
  if (state.status.type === "finished" || playerId !== state.activePlayerId) {
    return [];
  }

  const candidates: GameAction[] =
    state.schemaVersion === 5 ? [] : [{ type: "advance-phase", playerId }];
  const isActionWindow = state.schemaVersion === 5 && state.phase === "action-window";

  if (
    state.phase === "supply" &&
    (state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5) &&
    state.supplyStatus.type === "awaiting-upkeep-discard"
  ) {
    const supply = getSupplySnapshot(state);
    for (const laneId of LANE_IDS) {
      for (const armyCardInstanceId of supply.lanes[laneId].discardableArmyCardInstanceIds) {
        candidates.push({
          type: "discard-army-for-upkeep",
          playerId,
          laneId,
          armyCardInstanceId,
        });
      }
    }
  }

  if ((state.phase === "land" || isActionWindow) && isSetupGameState(state)) {
    const landCardInstanceIds = state.players[playerId].hand
      .filter((cardInstanceId) => getCardInstanceDefinition(state, cardInstanceId)?.type === "land")
      .sort(compareCodeUnits);

    if (state.players[playerId].actionsUsed.landActions === 0) {
      for (const cardInstanceId of landCardInstanceIds) {
        for (const laneId of LANE_IDS) {
          candidates.push({ type: "found-land", playerId, cardInstanceId, laneId });
        }
      }

      if (!hasFoundingPriority(state, playerId)) {
        for (const cardInstanceId of landCardInstanceIds) {
          for (const laneId of LANE_IDS) {
            candidates.push({ type: "develop-land", playerId, cardInstanceId, laneId });
          }
        }
      }
    }
  }

  if ((state.phase === "command" || isActionWindow) && isSetupGameState(state)) {
    for (const laneId of LANE_IDS) {
      const lane = state.lanes[laneId];
      const side = lane.players[playerId];
      if (lane.result !== "active" || side.status !== "active" || side.land === null) continue;

      if (side.commander !== null) {
        candidates.push({
          type: "disband-commander",
          playerId,
          laneId,
          commanderCardInstanceId: side.commander.cardInstanceId,
        });
      }

      const armyCardInstanceIds = side.armies
        .map((army) => army.cardInstanceId)
        .sort(compareCodeUnits);
      for (const armyCardInstanceId of armyCardInstanceIds) {
        candidates.push({ type: "disband-army", playerId, laneId, armyCardInstanceId });
      }
    }

    if (
      (state.schemaVersion === 3 || state.schemaVersion === 4 || state.schemaVersion === 5) &&
      (state.catalog.schemaVersion === 3 || state.catalog.schemaVersion === 4) &&
      state.players[playerId].actionsUsed.deploymentActions === 0
    ) {
      const commanderCardInstanceIds = state.players[playerId].hand
        .filter(
          (cardInstanceId) =>
            getCommanderDefinitionForInstance(state, cardInstanceId) !== undefined,
        )
        .sort(compareCodeUnits);

      for (const commanderCardInstanceId of commanderCardInstanceIds) {
        for (const laneId of LANE_IDS) {
          candidates.push({
            type: "deploy-commander",
            playerId,
            laneId,
            commanderCardInstanceId,
          });
          candidates.push({
            type: "replace-commander",
            playerId,
            laneId,
            commanderCardInstanceId,
          });
        }
      }

      const armyCardInstanceIds = state.players[playerId].hand
        .filter(
          (cardInstanceId) => getArmyDefinitionV3ForInstance(state, cardInstanceId) !== undefined,
        )
        .sort(compareCodeUnits);

      for (const armyCardInstanceId of armyCardInstanceIds) {
        for (const laneId of LANE_IDS) {
          candidates.push({ type: "deploy-army", playerId, laneId, armyCardInstanceId });
        }
      }
    }
  }

  if (state.phase === "battle" || isActionWindow) {
    const opponentPlayerId = playerId === "player-1" ? "player-2" : "player-1";
    for (const sourceLaneId of LANE_IDS) {
      for (const targetLaneId of getActiveLandLaneIds(state, opponentPlayerId)) {
        if (getAttackKind(state, playerId, sourceLaneId, targetLaneId) === null) continue;
        candidates.push({
          type: "attack",
          playerId,
          laneId: sourceLaneId,
          ...(state.schemaVersion === 4 || state.schemaVersion === 5 ? { targetLaneId } : {}),
        });
      }
    }
  }

  if (isActionWindow) {
    const eventCardInstanceIds = state.players[playerId].hand
      .filter(
        (cardInstanceId) => getEventDefinitionForInstance(state, cardInstanceId) !== undefined,
      )
      .sort(compareCodeUnits);
    for (const cardInstanceId of eventCardInstanceIds) {
      candidates.push({ type: "play-event", playerId, cardInstanceId });
    }
    candidates.push({ type: "end-turn", playerId });
  }

  return candidates.filter((action) => validateAction(state, action).valid);
}
